import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import util from '../../utils/util';
import { trailUpdate, TRAIL_MODULES } from '../../utils/activityTrail';
import {
    Card, Select, Button, Input, InputNumber, Row, Col,
    message, Table, Tag, Divider, Statistic,
    Descriptions, Progress, Alert, Tooltip, Collapse
} from 'antd';
import {
    EditOutlined, SaveOutlined, HistoryOutlined,
    StarFilled, StarOutlined, ReloadOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import FacilityService from '../../services/FacilityService';
import moment from 'moment';

const { Option } = Select;
const { Panel } = Collapse;

// ── Service checklist — WHO Harmonized Health Facility Assessment (HHFA)
// service-availability categories, cross-checked against Eswatini's NHSSP
// 2024/2025-2027/2028 priority service areas. ──────────────────────────────
const SERVICE_CATEGORIES = [
    { category: 'Outpatient & General', services: [
        'OPD (Outpatient Department)', 'Emergency / Casualty', 'Inpatient Wards', 'Surgery / Theatre',
    ]},
    { category: 'Reproductive, Maternal, Newborn & Child Health', services: [
        'Family Planning', 'ANC (Antenatal Care)', 'PMTCT (Prevention of Mother-to-Child Transmission)',
        'Labour & Delivery', 'Postnatal / Newborn Care', 'MCH / Maternity (general)',
        'Immunisation', 'Under-5 / Child Health Services', 'Adolescent Health Services',
    ]},
    { category: 'Communicable Diseases', services: [
        'ARV / HIV', 'TB Treatment', 'Malaria', 'STIs (other than HIV)', 'Neglected Tropical Diseases (NTDs)',
    ]},
    { category: 'Non-Communicable Diseases', services: [
        'Diabetes', 'Cardiovascular Disease / Hypertension', 'Chronic Respiratory Disease (Asthma/COPD)',
        'Cancer Screening & Treatment',
    ]},
    { category: 'Mental & Neurological Health', services: [
        'Mental Health', 'Neurological Conditions (epilepsy, dementia)',
    ]},
    { category: 'Special Needs / Protection', services: [
        'Palliative Care', 'Rehabilitation / Physiotherapy',
        'GBV / Rape & Intimate Partner Violence Survivor Care', 'Services for Children Affected by Maltreatment',
    ]},
    { category: 'Diagnostics & Support Services', services: [
        'Laboratory', 'Radiology / X-Ray / Imaging', 'Blood Transfusion', 'Pharmacy',
    ]},
    { category: 'Other', services: [
        'Dental / Oral Health', 'Nutrition (Clinical Nutrition/Dietetics)',
    ]},
];
const ALL_SERVICES = SERVICE_CATEGORIES.flatMap(c => c.services);

// A service entry can be: undefined, a legacy plain string ('Yes'/'No'),
// a legacy array (old flat SERVICES list), or the new { value, notes } shape.
// This normalizes all of them into one consistent { value, notes } object —
// existing saved profiles keep displaying correctly with no migration needed.
const getServiceEntry = (so, service) => {
    if (!so) return { value: '', notes: '' };
    if (Array.isArray(so)) return { value: so.includes(service) ? 'Yes' : '', notes: '' };
    const v = so[service];
    if (v == null) return { value: '', notes: '' };
    if (typeof v === 'string') return { value: v, notes: '' };
    return { value: v.value || '', notes: v.notes || '' };
};

// ── Staffing — per-cadre breakdown, matching the cadre naming already
// established in the platform's other HR-facing tools (TLC investigation
// tool's D1/D2 staffing sections). ──────────────────────────────────────────
const CADRES = [
    'Medical Doctors', 'Registered Nurses', 'Enrolled Nurses', 'Midwives',
    'Responsible Pharmacist', 'Pharmacist', 'Pharmacist Intern', 'Pharmacy Technician', 'Pharmacy Assistant',
    'Laboratory Staff', 'Allied Health Staff', 'Support Staff',
];
const getCadreEntry = (sbc, cadre) => {
    if (!sbc || !sbc[cadre]) return { approved: '', actual: '' };
    return { approved: sbc[cadre].approved ?? '', actual: sbc[cadre].actual ?? '' };
};

const OWNERSHIP_TYPES = ['Government','UEDF','Mission','Private','NGO','Other'];
const ACCRED_STATUSES = ['Not Accredited','ISO 9001:2015 Certified','ISO 15189 Certified',
    'JCI Accredited','QoC Baseline Complete','QoC Accreditation In Progress'];

const RATING = [
    { min:98, label:'Outstanding',          stars:5, color:'#1b5e20' },
    { min:95, label:'Excellent',            stars:4, color:'#1565c0' },
    { min:90, label:'Good',                 stars:3, color:'#2e7d32' },
    { min:85, label:'Fair',                 stars:2, color:'#f9a825' },
    { min:70, label:'Requires Improvement', stars:1, color:'#e64a19' },
    { min:0,  label:'Inadequate',           stars:0, color:'#b71c1c' },
];
const getRating = s => RATING.find(r => s >= r.min) || RATING[RATING.length-1];

function Stars({ count, size=12 }) {
    return <span>{[1,2,3,4,5].map(i => (
        i <= count
            ? <StarFilled  key={i} style={{ color:'#f9a825', fontSize:size }}/>
            : <StarOutlined key={i} style={{ color:'#e0e0e0', fontSize:size }}/>
    ))}</span>;
}

export default function QoCFacilityProfile() {
    const [facilities, setFacilities] = useState([]);
    // Permission checks
    const canAdd    = util.canAdd('qoc_facility_profile');
    const canEdit   = util.canEdit('qoc_facility_profile');
    const canDelete = util.canDelete('qoc_facility_profile');
    const canExport = util.canExport('qoc_facility_profile');
    const [selected, setSelected]     = useState(null);
    const location = useLocation();
    useEffect(() => {
        const facilityParam = new URLSearchParams(location.search).get('facility');
        if (facilityParam) setSelected(parseInt(facilityParam));
    }, []); // eslint-disable-line
    const [profile, setProfile]       = useState(null);
    const [editing, setEditing]       = useState(false);
    const [form, setForm]             = useState({});
    const [loading, setLoading]       = useState(false);
    const [saving, setSaving]         = useState(false);

    useEffect(() => {
        FacilityService.all({ status:1 }).then(r =>
            setFacilities(r.data?.result?.data || []));
    }, []);

    useEffect(() => {
        if (selected) loadProfile();
    }, [selected]); // eslint-disable-line

    const loadProfile = async () => {
        setLoading(true);
        try {
            const res = await QoCService.getFacilityProfile(selected);
            const p   = res.data?.data || {};
            setProfile(p);
            setForm({
                ownership_type:       p.ownership_type || 'Government',
                catchment_population: p.catchment_population || '',
                num_beds:             p.num_beds || '',
                services_offered:     p.services_offered || {},
                staffing_by_cadre:    p.staffing_by_cadre || {},
                implementing_partners:p.implementing_partners || '',
                accreditation_status: p.accreditation_status || 'Not Accredited',
            });
        } catch { message.error('Failed to load profile'); }
        finally { setLoading(false); }
    };

    const setServiceValue = (service, field, value) => {
        setForm(f => {
            const cur = getServiceEntry(f.services_offered, service);
            return {
                ...f,
                services_offered: {
                    ...(Array.isArray(f.services_offered) ? {} : f.services_offered),
                    [service]: { ...cur, [field]: value },
                },
            };
        });
    };

    const setCadreValue = (cadre, field, value) => {
        setForm(f => {
            const cur = getCadreEntry(f.staffing_by_cadre, cadre);
            return {
                ...f,
                staffing_by_cadre: {
                    ...(f.staffing_by_cadre || {}),
                    [cadre]: { ...cur, [field]: value },
                },
            };
        });
    };

    const cadreTotals = (sbc) => {
        let approved = 0, actual = 0;
        CADRES.forEach(c => {
            const e = getCadreEntry(sbc, c);
            approved += Number(e.approved) || 0;
            actual   += Number(e.actual) || 0;
        });
        return { approved, actual };
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const totals = cadreTotals(form.staffing_by_cadre);
            const payload = {
                ...form,
                staffing_approved: totals.approved,
                staffing_actual:   totals.actual,
            };
            await QoCService.saveFacilityProfile(selected, payload);

            const facilityName = facilities.find(f => f.id === selected)?.business_name || String(selected);
            trailUpdate(TRAIL_MODULES.FACILITIES, facilityName, selected, {
                ownership_type:       form.ownership_type,
                accreditation_status: form.accreditation_status,
                catchment_population: form.catchment_population,
                num_beds:             form.num_beds,
                staffing_approved:    totals.approved,
                staffing_actual:      totals.actual,
            });
            message.success('Facility profile updated');
            setEditing(false);
            loadProfile();
        } catch { message.error('Save failed'); }
        finally { setSaving(false); }
    };

    const histCols = [
        { title:'Audit Date', dataIndex:'assessment_date', width:110,
          render:d => moment(d).format('DD MMM YYYY') },
        { title:'Clauses', dataIndex:'clauses', width:200,
          render:v => v ? v.split(',').map(c => (
            <Tag key={c.trim()} style={{ fontSize:9, margin:1 }}>{c.trim()}</Tag>
          )) : '-' },
        { title:'Score', width:90, dataIndex:'overall_percentage',
          render:s => {
            const r = getRating(parseFloat(s||0));
            return <span style={{ fontWeight:900, color:r.color }}>{parseFloat(s||0).toFixed(1)}%</span>;
          }},
        { title:'Rating', width:130,
          render:(_,row) => {
            const r = getRating(parseFloat(row.overall_percentage||0));
            return <span style={{ color:r.color, fontWeight:700, fontSize:11 }}>
                <Stars count={r.stars} size={10}/> {r.label}
            </span>;
          }},
        { title:'Assessor', dataIndex:'assessor_name', width:130 },
        { title:'Status', dataIndex:'status', width:90,
          render:s => <Tag color={s==='approved'?'green':'blue'} style={{ fontSize:10 }}>{s}</Tag> },
    ];

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Facility Profile</h2>
                    <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                        Extended facility information that enriches the narrative audit report
                    </div>
                </div>
            </div>

            <div className="page-pad">
                <Card size="small" style={{ marginBottom:16 }}>
                    <Row gutter={12} align="middle">
                        <Col span={10}>
                            <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>
                                Select Facility
                            </label>
                            <Select showSearch optionFilterProp="children"
                                placeholder="Search facility..." style={{ width:'100%' }}
                                value={selected} onChange={v => { setSelected(v); setEditing(false); }}>
                                {facilities.map(f => (
                                    <Option key={f.id} value={f.id}>{f.business_name}</Option>
                                ))}
                            </Select>
                        </Col>
                        {profile && (
                            <Col span={14} style={{ display:'flex', gap:12, alignItems:'center', paddingTop:20 }}>
                                {profile.last_audit_score && (() => {
                                    const r = getRating(parseFloat(profile.last_audit_score));
                                    return (
                                        <div style={{ background:`${r.color}10`, border:`1px solid ${r.color}30`,
                                            borderRadius:8, padding:'6px 12px' }}>
                                            <div style={{ fontSize:9, color:'#888', fontWeight:700 }}>LAST AUDIT</div>
                                            <div style={{ fontWeight:900, fontSize:16, color:r.color }}>
                                                {parseFloat(profile.last_audit_score).toFixed(1)}%
                                            </div>
                                            <Stars count={r.stars} size={11}/>
                                        </div>
                                    );
                                })()}
                                {profile.cap_summary && (
                                    <div style={{ background:'#fff7e6', border:'1px solid #ffd591',
                                        borderRadius:8, padding:'6px 12px' }}>
                                        <div style={{ fontSize:9, color:'#888', fontWeight:700 }}>OPEN CAPs</div>
                                        <div style={{ fontWeight:900, fontSize:16,
                                            color: (profile.cap_summary.open + profile.cap_summary.overdue) > 0 ? '#f5222d' : '#52c41a' }}>
                                            {(profile.cap_summary.open||0) + (profile.cap_summary.overdue||0)}
                                        </div>
                                        <div style={{ fontSize:9, color:'#888' }}>
                                            {profile.cap_summary.closed||0} closed
                                        </div>
                                    </div>
                                )}
                            </Col>
                        )}
                    </Row>
                </Card>

                {profile && (
                    <>
                    {/* Profile details */}
                    <Card size="small" style={{ marginBottom:16 }}
                        title={<span>Facility Details</span>}
                        extra={
                            editing
                                ? <Button type="primary" icon={<SaveOutlined/>}
                                    loading={saving} onClick={handleSave}
                                    style={{ background:'#52c41a', borderColor:'#52c41a' }}>
                                    Save Profile
                                  </Button>
                                : <Button icon={<EditOutlined/>} onClick={() => setEditing(true)}>
                                    Edit Profile
                                  </Button>
                        }>
                        {!editing ? (
                            <>
                            <Descriptions column={3} size="small" bordered>
                                <Descriptions.Item label="Facility Name" span={2}>
                                    <strong>{profile.business_name}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="Level">{profile.site_type || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Region">{profile.located_region || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Ownership">{profile.ownership_type || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Accreditation">{profile.accreditation_status || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Catchment Population">
                                    {profile.catchment_population ? profile.catchment_population.toLocaleString() : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Bed Capacity">{profile.num_beds || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Overall Staffing (Approved / Actual)" span={2}>
                                    {profile.staffing_approved || '-'} / {profile.staffing_actual || '-'}
                                    {profile.staffing_approved && profile.staffing_actual && (
                                        <span style={{ marginLeft:8, color: profile.staffing_actual/profile.staffing_approved < 0.7 ? '#f5222d' : '#52c41a', fontSize:11 }}>
                                            ({Math.round(profile.staffing_actual/profile.staffing_approved*100)}% filled)
                                        </span>
                                    )}
                                    <span style={{ marginLeft:10, fontSize:10, color:'#aaa' }}>
                                        — see cadre breakdown below
                                    </span>
                                </Descriptions.Item>
                                <Descriptions.Item label="Implementing Partners" span={3}>
                                    {profile.implementing_partners || '-'}
                                </Descriptions.Item>
                            </Descriptions>

                            {/* Staffing by cadre — read-only */}
                            <Divider orientation="left" style={{ fontSize:12, marginTop:20 }}>Staffing by Cadre</Divider>
                            <Table size="small" pagination={false} rowKey="cadre"
                                dataSource={CADRES.map(c => {
                                    const e = getCadreEntry(profile.staffing_by_cadre, c);
                                    const pct = e.approved ? Math.round((Number(e.actual)||0) / Number(e.approved) * 100) : null;
                                    return { cadre: c, ...e, pct };
                                }).filter(r => r.approved !== '' || r.actual !== '')}
                                columns={[
                                    { title:'Cadre', dataIndex:'cadre' },
                                    { title:'Approved', dataIndex:'approved', width:100, align:'center' },
                                    { title:'Actual', dataIndex:'actual', width:100, align:'center' },
                                    { title:'% Filled', width:120, render:(_,r) => r.pct == null ? '-' : (
                                        <span style={{ color: r.pct < 70 ? '#f5222d' : '#52c41a', fontWeight:700 }}>{r.pct}%</span>
                                    )},
                                ]}
                                locale={{ emptyText: 'No cadre-level staffing captured yet' }}/>

                            {/* Services offered — grouped by category, read-only */}
                            <Divider orientation="left" style={{ fontSize:12, marginTop:20 }}>Services Offered</Divider>
                            <Collapse defaultActiveKey={SERVICE_CATEGORIES.map((_,i)=>String(i))} size="small">
                                {SERVICE_CATEGORIES.map((cat, i) => {
                                    const entries = cat.services.map(s => ({ service: s, ...getServiceEntry(profile.services_offered, s) }));
                                    const anySet = entries.some(e => e.value);
                                    return (
                                        <Panel header={cat.category} key={String(i)}>
                                            {entries.map(e => (
                                                <div key={e.service} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                                                    <span style={{ fontSize:12, flex:1 }}>{e.service}</span>
                                                    {e.value ? (
                                                        <Tag color={e.value==='Yes'?'success':e.value==='No'?'error':'default'} style={{ margin:0 }}>
                                                            {e.value}
                                                        </Tag>
                                                    ) : <span style={{ color:'#ccc', fontSize:11 }}>Not captured</span>}
                                                    {e.notes && (
                                                        <Tooltip title={e.notes}>
                                                            <InfoCircleOutlined style={{ color:'#888' }}/>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            ))}
                                            {!anySet && <span style={{ color:'#ccc', fontSize:11 }}>Nothing captured in this category yet</span>}
                                        </Panel>
                                    );
                                })}
                            </Collapse>
                            </>
                        ) : (
                            <Row gutter={12}>
                                <Col span={8}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Ownership Type</label>
                                    <Select style={{ width:'100%' }} value={form.ownership_type}
                                        onChange={v => setForm(f => ({ ...f, ownership_type:v }))}>
                                        {OWNERSHIP_TYPES.map(o => <Option key={o} value={o}>{o}</Option>)}
                                    </Select>
                                </Col>
                                <Col span={8}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Accreditation Status</label>
                                    <Select style={{ width:'100%' }} value={form.accreditation_status}
                                        onChange={v => setForm(f => ({ ...f, accreditation_status:v }))}>
                                        {ACCRED_STATUSES.map(s => <Option key={s} value={s}>{s}</Option>)}
                                    </Select>
                                </Col>
                                <Col span={8}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Catchment Population</label>
                                    <InputNumber style={{ width:'100%' }} min={0}
                                        value={form.catchment_population}
                                        onChange={v => setForm(f => ({ ...f, catchment_population:v }))}
                                        formatter={v => v ? parseInt(v).toLocaleString() : ''}/>
                                </Col>
                                <Col span={8} style={{ marginTop:10 }}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Bed Capacity</label>
                                    <InputNumber style={{ width:'100%' }} min={0}
                                        value={form.num_beds}
                                        onChange={v => setForm(f => ({ ...f, num_beds:v }))}/>
                                </Col>
                                <Col span={16} style={{ marginTop:10 }}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Implementing Partners</label>
                                    <Input value={form.implementing_partners}
                                        onChange={e => setForm(f => ({ ...f, implementing_partners:e.target.value }))}
                                        placeholder="e.g. MSF, UNICEF, PEPFAR"/>
                                </Col>

                                {/* Staffing by cadre — edit */}
                                <Col span={24} style={{ marginTop:16 }}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:8 }}>
                                        Staffing by Cadre
                                    </label>
                                    <div style={{ border:'1px solid #f0f0f0', borderRadius:8, overflow:'hidden' }}>
                                        <div style={{ display:'grid', gridTemplateColumns:'1fr 120px 120px 100px',
                                            background:'#f5f5f5', padding:'6px 12px', fontSize:11, fontWeight:700, color:'#666' }}>
                                            <span>Cadre</span>
                                            <span style={{ textAlign:'center' }}>Approved</span>
                                            <span style={{ textAlign:'center' }}>Actual</span>
                                            <span style={{ textAlign:'center' }}>% Filled</span>
                                        </div>
                                        {CADRES.map((c, i) => {
                                            const e = getCadreEntry(form.staffing_by_cadre, c);
                                            const pct = e.approved ? Math.round((Number(e.actual)||0) / Number(e.approved) * 100) : null;
                                            return (
                                                <div key={c} style={{
                                                    display:'grid', gridTemplateColumns:'1fr 120px 120px 100px',
                                                    padding:'6px 12px', alignItems:'center',
                                                    background: i%2 ? '#fafafa' : '#fff',
                                                    borderTop: i>0 ? '1px solid #f0f0f0' : 'none',
                                                }}>
                                                    <span style={{ fontSize:12 }}>{c}</span>
                                                    <InputNumber size="small" min={0} style={{ width:'100%' }}
                                                        value={e.approved}
                                                        onChange={v => setCadreValue(c, 'approved', v)}/>
                                                    <InputNumber size="small" min={0} style={{ width:'100%', marginLeft:8 }}
                                                        value={e.actual}
                                                        onChange={v => setCadreValue(c, 'actual', v)}/>
                                                    <span style={{ textAlign:'center', fontSize:11,
                                                        color: pct == null ? '#ccc' : pct < 70 ? '#f5222d' : '#52c41a', fontWeight:700 }}>
                                                        {pct == null ? '-' : `${pct}%`}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                        {(() => {
                                            const t = cadreTotals(form.staffing_by_cadre);
                                            const pct = t.approved ? Math.round(t.actual / t.approved * 100) : null;
                                            return (
                                                <div style={{ display:'grid', gridTemplateColumns:'1fr 120px 120px 100px',
                                                    padding:'8px 12px', background:'#eef7ee', borderTop:'2px solid #d9d9d9', fontWeight:700 }}>
                                                    <span style={{ fontSize:12 }}>Total</span>
                                                    <span style={{ textAlign:'center', fontSize:12 }}>{t.approved}</span>
                                                    <span style={{ textAlign:'center', fontSize:12 }}>{t.actual}</span>
                                                    <span style={{ textAlign:'center', fontSize:12, color: pct == null ? '#ccc' : pct < 70 ? '#f5222d' : '#52c41a' }}>
                                                        {pct == null ? '-' : `${pct}%`}
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </Col>

                                {/* Services offered — edit, grouped by category with notes */}
                                <Col span={24} style={{ marginTop:16 }}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:8 }}>
                                        Services Offered
                                        <span style={{ fontWeight:400, fontSize:11, color:'#888', marginLeft:8 }}>
                                            Based on the WHO Harmonized Health Facility Assessment, cross-checked against Eswatini's NHSSP 2024/2025–2027/2028
                                        </span>
                                    </label>
                                    <Collapse defaultActiveKey={['0']} size="small">
                                        {SERVICE_CATEGORIES.map((cat, ci) => (
                                            <Panel header={cat.category} key={String(ci)}>
                                                <div style={{ border:'1px solid #f0f0f0', borderRadius:8, overflow:'hidden' }}>
                                                    <div style={{ display:'grid', gridTemplateColumns:'1.6fr 70px 70px 70px 1.4fr',
                                                        background:'#f5f5f5', padding:'6px 12px', fontSize:11, fontWeight:700, color:'#666' }}>
                                                        <span>Service</span>
                                                        <span style={{ textAlign:'center', color:'#52c41a' }}>Yes</span>
                                                        <span style={{ textAlign:'center', color:'#f5222d' }}>No</span>
                                                        <span style={{ textAlign:'center', color:'#888' }}>N/A</span>
                                                        <span>Notes</span>
                                                    </div>
                                                    {cat.services.map((s, i) => {
                                                        const e = getServiceEntry(form.services_offered, s);
                                                        return (
                                                            <div key={s} style={{
                                                                display:'grid', gridTemplateColumns:'1.6fr 70px 70px 70px 1.4fr',
                                                                padding:'7px 12px', alignItems:'center',
                                                                background: i%2 ? '#fafafa' : '#fff',
                                                                borderTop: i>0 ? '1px solid #f0f0f0' : 'none',
                                                            }}>
                                                                <span style={{ fontSize:12 }}>{s}</span>
                                                                {['Yes','No','N/A'].map(opt => (
                                                                    <div key={opt} style={{ textAlign:'center' }}>
                                                                        <div onClick={() => setServiceValue(s, 'value', e.value===opt ? '' : opt)} style={{
                                                                            width:20, height:20, borderRadius:'50%',
                                                                            border:`2px solid ${
                                                                                e.value===opt
                                                                                    ? (opt==='Yes'?'#52c41a':opt==='No'?'#f5222d':'#888')
                                                                                    : '#d9d9d9'
                                                                            }`,
                                                                            background: e.value===opt
                                                                                ? (opt==='Yes'?'#52c41a':opt==='No'?'#f5222d':'#888')
                                                                                : 'transparent',
                                                                            margin:'0 auto', cursor:'pointer',
                                                                            display:'flex', alignItems:'center', justifyContent:'center',
                                                                        }}>
                                                                            {e.value===opt && <div style={{ width:9, height:9, borderRadius:'50%', background:'#fff' }}/>}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                                <Input size="small" placeholder="Optional note..."
                                                                    value={e.notes}
                                                                    onChange={ev => setServiceValue(s, 'notes', ev.target.value)}/>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </Panel>
                                        ))}
                                    </Collapse>
                                </Col>
                            </Row>
                        )}
                    </Card>

                    {/* Audit history */}
                    {profile.audit_history?.length > 0 && (
                        <Card size="small"
                            title={<span><HistoryOutlined style={{ marginRight:6 }}/>
                                Audit History ({profile.audit_history.length} audits)</span>}>
                            <Table columns={histCols}
                                dataSource={profile.audit_history}
                                rowKey="id" size="small"
                                pagination={{ pageSize:10, size:'small' }}/>
                        </Card>
                    )}
                    </>
                )}

                {!selected && (
                    <div style={{ textAlign:'center', padding:60, color:'#aaa' }}>
                        <div style={{ fontSize:48, color:'#ccc' }}>Facility Profile</div>
                        <div style={{ fontSize:14, marginTop:8 }}>
                            Select a facility above to view and edit its profile
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}




